<template>
    <div class="pa-4">
        <v-row dense>
            <v-col cols="12" sm="6" md="2">
                <v-select
                    v-model="filtros.cenario"
                    :items="opcoesCenario"
                    label="Cenário"
                    outlined
                    dense
                    hide-details
                    @change="carregar(1)"
                />
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <v-select
                    v-model="filtros.situacao"
                    :items="opcoesSituacao"
                    label="Situação"
                    outlined
                    dense
                    hide-details
                    @change="carregar(1)"
                />
            </v-col>
            <v-col cols="12" sm="6" md="2">
                <v-select
                    v-model="filtros.onda"
                    :items="opcoesOnda"
                    label="Onda"
                    outlined
                    dense
                    hide-details
                    @change="carregar(1)"
                />
            </v-col>
            <v-col cols="12" sm="6" md="5">
                <v-text-field
                    v-model="filtros.busca"
                    label="Empresa (nome, domínio ou id)"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    hide-details
                    clearable
                    @keypress.enter="carregar(1)"
                    @click:clear="filtros.busca = ''; carregar(1)"
                />
            </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mt-3" style="gap: 8px;">
            <span class="body-2 grey--text text--darken-2">
                {{ selecionadas.length ? `${selecionadas.length} selecionada(s)` : `${total} empresa(s)` }}
            </span>
            <v-spacer/>
            <template v-if="podeLiberar">
                <v-btn small color="primary" :disabled="!selecionadas.length" @click="dialogOnda = true">
                    <v-icon left small>mdi-rocket-launch-outline</v-icon>Liberar onda
                </v-btn>
                <v-btn small outlined color="primary" :disabled="!selecionadas.length" :loading="acao === 'pausar'" @click="emLote('pausar')">
                    <v-icon left small>mdi-pause</v-icon>Pausar
                </v-btn>
                <v-btn small outlined color="primary" :disabled="!selecionadas.length" :loading="acao === 'retomar'" @click="emLote('retomar')">
                    <v-icon left small>mdi-play</v-icon>Retomar
                </v-btn>
            </template>
        </div>

        <v-data-table
            v-model="selecionadas"
            :headers="headers"
            :items="itens"
            item-key="empresa_id"
            show-select
            :mobileBreakpoint="0"
            :loading="loading"
            :items-per-page="porPagina"
            :server-items-length="total"
            :page="pagina"
            :footer-props="{ itemsPerPageOptions: [50, 100, 200] }"
            class="mt-3"
            dense
            no-data-text="Nenhuma empresa com esses filtros"
            @update:page="carregar($event)"
            @update:items-per-page="porPagina = $event; carregar(1)"
        >
            <template v-slot:item.fantasia="{ item }">
                <div class="font-weight-medium">{{ item.fantasia || `Empresa ${item.empresa_id}` }}</div>
                <div class="caption grey--text">#{{ item.empresa_id }} · {{ item.dominio }}</div>
            </template>

            <template v-slot:item.cenario="{ item }">
                <v-chip x-small label dark :color="cenario(item.cenario).cor">{{ cenario(item.cenario).texto }}</v-chip>
            </template>

            <template v-slot:item.situacao="{ item }">
                <v-chip x-small label dark :color="situacao(item.situacao).cor">{{ situacao(item.situacao).texto }}</v-chip>
                <div v-if="item.status === 'liberada'" class="caption grey--text">
                    {{ item.modo === 'obrigatoria' ? 'obrigatória' : `convite · ${item.adiamentos}/${item.adiamentos_max} adiamentos` }}
                </div>
            </template>

            <template v-slot:item.titular="{ item }">
                <template v-if="item.outra_conta_liberada_em">
                    <div class="body-2">Outra conta liberada</div>
                    <div class="caption grey--text" :title="item.outra_conta_motivo">{{ item.outra_conta_motivo }}</div>
                </template>
                <template v-else-if="item.titular">
                    <div class="body-2">{{ item.titular.nome || '—' }}</div>
                    <div class="caption grey--text">{{ contaTexto(item.titular) }}</div>
                </template>
                <span v-else class="grey--text">—</span>
            </template>

            <template v-slot:item.tentativa="{ item }">
                <template v-if="item.tentativa">
                    <div class="body-2">{{ (item.tentativa.conta && item.tentativa.conta.nome) || 'conta sem titular' }}</div>
                    <div class="caption grey--text">
                        {{ formatDate(item.tentativa.em, 'DD/MM HH:mm') }}<span v-if="item.tentativa.usuario"> · {{ item.tentativa.usuario }}</span>
                    </div>
                </template>
                <span v-else class="grey--text">—</span>
            </template>

            <template v-slot:item.nao_responsavel="{ item }">
                <span :class="{ 'orange--text text--darken-3 font-weight-medium': item.nao_responsavel >= 2 }">{{ item.nao_responsavel || '—' }}</span>
            </template>

            <template v-slot:item.volume_pix_30d_antes="{ item }">{{ reais(item.volume_pix_30d_antes) }}</template>

            <template v-slot:item.liberada_em="{ item }">
                <div>{{ formatDate(item.liberada_em, 'DD/MM HH:mm') || '—' }}</div>
                <div v-if="item.onda" class="caption grey--text">{{ item.onda }}</div>
            </template>

            <template v-slot:item.acoes="{ item }">
                <div class="d-flex justify-end" style="gap: 4px;">
                    <v-btn
                        v-if="item.pode_liberar_outra && podeOutraConta"
                        x-small
                        outlined
                        color="primary"
                        @click="outraConta = item"
                    >
                        <v-icon left x-small>mdi-account-switch-outline</v-icon>Liberar outra conta
                    </v-btn>
                    <v-btn
                        v-if="item.pode_desfazer && podeDesfazer"
                        x-small
                        text
                        :loading="acao === `desfazer-${item.empresa_id}`"
                        @click="desfazer(item)"
                    >
                        <v-icon left x-small>mdi-undo</v-icon>Desfazer
                    </v-btn>
                </div>
            </template>
        </v-data-table>

        <LiberarOndaDialog
            v-if="dialogOnda"
            :empresas="selecionadas"
            :ondas="ondas"
            @close="dialogOnda = false"
            @liberou="aposAcao"
        />

        <LiberarOutraContaDialog
            v-if="outraConta"
            :item="outraConta"
            @close="outraConta = null"
            @liberou="aposAcao"
        />
    </div>
</template>

<script>
import LiberarOndaDialog from './LiberarOndaDialog';
import LiberarOutraContaDialog from './LiberarOutraContaDialog';
import { cenario, situacao, reais, contaTexto, OPCOES_CENARIO, OPCOES_SITUACAO } from './situacoes';

export default {
    name: 'TabEmpresas',

    components: { LiberarOndaDialog, LiberarOutraContaDialog },

    props: {
        ondas: { type: Array, default: () => [] },
    },

    data: () => ({
        loading: false,
        itens: [],
        total: 0,
        pagina: 1,
        porPagina: 50,
        selecionadas: [],
        filtros: { cenario: null, situacao: 'pendentes', onda: null, busca: '' },
        opcoesCenario: OPCOES_CENARIO,
        opcoesSituacao: OPCOES_SITUACAO,
        dialogOnda: false,
        outraConta: null,
        acao: null,
        consulta: 0,
        headers: [
            { text: 'Empresa', value: 'fantasia', sortable: false },
            { text: 'Cenário', value: 'cenario', sortable: false },
            { text: 'Situação', value: 'situacao', sortable: false },
            { text: 'Titular esperado', value: 'titular', sortable: false },
            { text: 'Tentativa bloqueada', value: 'tentativa', sortable: false },
            { text: '"Não sou o responsável"', value: 'nao_responsavel', sortable: false, align: 'center' },
            { text: 'PIX em 30 dias', value: 'volume_pix_30d_antes', sortable: false },
            { text: 'Liberada em', value: 'liberada_em', sortable: false },
            { text: '', value: 'acoes', sortable: false, align: 'end' },
        ],
    }),

    computed: {
        opcoesOnda() {
            return [{ text: 'Todas', value: null }, ...this.ondas.map(o => ({ text: o, value: o }))];
        },

        podeLiberar() {
            return this.checkPermission('zapercontrol_mp_migracao_liberar') || this.isDiretor();
        },

        podeOutraConta() {
            return this.checkPermission('zapercontrol_mp_migracao_outra_conta') || this.isDiretor();
        },

        podeDesfazer() {
            return this.checkPermission('zapercontrol_mp_migracao_desfazer') || this.isDiretor();
        },
    },

    mounted() {
        this.carregar(1);
    },

    methods: {
        cenario,
        situacao,
        reais,
        contaTexto,

        filtrar(filtros) {
            this.filtros = { ...this.filtros, ...filtros };
            this.carregar(1);
        },

        async carregar(pagina = this.pagina) {
            this.loading = true;
            this.pagina = pagina;
            // Só a última consulta vale: um filtro trocado no meio não é sobrescrito pela resposta anterior.
            const consulta = ++this.consulta;

            const params = {
                page: pagina,
                por_pagina: this.porPagina,
                situacao: this.filtros.situacao,
                cenario: this.filtros.cenario || undefined,
                onda: this.filtros.onda || undefined,
                busca: this.filtros.busca || undefined,
            };

            try {
                const { data } = await this.$http.get('mercadopago-migracao', { params });

                if (consulta !== this.consulta) {
                    return;
                }

                if (data.type !== 'success') {
                    this.notify(data.msg || 'Não foi possível carregar as empresas', 'warning');
                    return;
                }

                this.itens = data.data.data;
                this.total = data.data.total;
                this.selecionadas = [];
            } catch (e) {
                consulta === this.consulta && this.notify('Não foi possível carregar as empresas', 'warning');
            } finally {
                if (consulta === this.consulta) {
                    this.loading = false;
                }
            }
        },

        async emLote(acao) {
            this.acao = acao;

            try {
                const { data } = await this.$http.post(`mercadopago-migracao/${acao}`, {
                    empresas: this.selecionadas.map(i => i.empresa_id),
                });

                this.notify(data.msg, data.type === 'success' ? 'success' : 'warning');
                data.type === 'success' && this.aposAcao();
            } catch (e) {
                this.notify('Verifique sua conexão com a internet', 'warning');
            } finally {
                this.acao = null;
            }
        },

        async desfazer(item) {
            const ok = await this.$root.$confirm(
                'Desfazer migração',
                `A ${item.fantasia || `empresa ${item.empresa_id}`} volta para a conexão de antes da migração. Continuar?`,
                { color: 'red darken-1' },
            );
            if (!ok) {
                return;
            }

            this.acao = `desfazer-${item.empresa_id}`;

            try {
                const { data } = await this.$http.post(`mercadopago-migracao/${item.empresa_id}/desfazer`);
                this.notify(data.msg, data.type === 'success' ? 'success' : 'warning');
                data.type === 'success' && this.aposAcao();
            } catch (e) {
                this.notify('Verifique sua conexão com a internet', 'warning');
            } finally {
                this.acao = null;
            }
        },

        aposAcao() {
            this.dialogOnda = false;
            this.outraConta = null;
            this.carregar();
            this.$emit('mudou');
        },
    },
};
</script>
